import { ROLE_PRESETS, type RolePreset } from '@erp/shared';
import { Check, KeyRound, Pencil, Plus, ShieldCheck, Users } from 'lucide-react';
import type * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { Badge, Button, Card, EmptyState, Skeleton } from '../../components/ui/index.js';
import { usePermission } from '../../hooks/usePermission.js';
import { api } from '../../lib/api.js';
import { useAuthStore } from '../../lib/authStore.js';
import { moduleEnabled, moduleForPermission } from '../../lib/modules.js';
import { useEnabledModules } from '../../lib/modulesStore.js';

/**
 * A role as the Roles screen needs it — `GET /roles?detail=1`.
 *
 * The two detail fields are optional on purpose: `detail=1` is a newer
 * parameter, and during a rolling deploy this bundle can be talking to an API
 * that predates it and answers with the plain `{id, code, name}` shape. Typing
 * them as present turns that ordinary window into a blank page.
 */
interface RoleDetail {
  id: string;
  code: string;
  name: string;
  permissionCodes?: string[];
  userCount?: number;
}

/**
 * Roles the app itself looks up by literal code. Renaming or deleting one of
 * these silently breaks a guard somewhere else, so the screen marks them and
 * (once the editor lands) refuses to touch them: `owner` is checked by the
 * last-admin guard AND by the platform's reset-owner-password hatch, `mandob`
 * by ensureMandobRole, `general_manager` by the profits migration.
 */
const SYSTEM_ROLE_CODES = new Set(['owner', 'admin', 'member', 'cashier', 'mandob', 'general_manager']);

/**
 * Render a permission code as words.
 *
 * Built from a domain vocabulary plus an action vocabulary rather than 141
 * hand-written strings, so `stock.transfer.read` reads as "Stock — Between
 * warehouses · View" and a code added tomorrow still renders sensibly instead
 * of showing a raw dotted string to a shop owner.
 */
function usePermissionLabels(): {
  groupOf: (code: string) => string;
  labelOf: (code: string) => string;
} {
  const { t } = useTranslation('permissions');
  return useMemo(() => ({
    groupOf: (code) => {
      const domain = code.split('.')[0] ?? code;
      return t(`groups.${domain}`, { defaultValue: domain.replace(/_/g, ' ') });
    },
    labelOf: (code) => {
      // A code whose generic words would mislead gets a label of its own:
      // stock.report would otherwise read as the `report` action's "Whole-shop
      // report", when a branch-scoped person sees their branches only.
      const own = t(`codes.${code.replace(/\./g, '_')}`, { defaultValue: '' });
      if (own) return own;
      const parts = code.split('.').slice(1);
      if (parts.length === 0) return code;
      return parts
        .map((p) => t(`actions.${p}`, { defaultValue: p.replace(/_/g, ' ') }))
        .join(' · ');
    },
  }), [t]);
}

/**
 * What a role grants, grouped by domain.
 *
 * Read-only until `onToggle` is supplied, because most visits are somebody
 * checking what a role does rather than changing it — and a screen full of live
 * checkboxes invites an accidental click on something load-bearing.
 *
 * When editing, a code the viewer does not hold is shown DISABLED rather than
 * hidden. The server refuses to grant or revoke it either way; hiding it would
 * make the role look smaller than it is, which is worse than showing a switch
 * that cannot move.
 */
function PermissionGrid({
  codes,
  all,
  held,
  onToggle,
  moduleOff,
}: {
  codes: readonly string[];
  all?: readonly string[];
  held?: readonly string[];
  onToggle?: (code: string) => void;
  /** True when the code belongs to a module this shop has switched off. */
  moduleOff?: (code: string) => boolean;
}): React.JSX.Element {
  const { groupOf, labelOf } = usePermissionLabels();
  const editing = !!onToggle;
  const granted = useMemo(() => new Set(codes), [codes]);
  // Permissions for a switched-off module are dropped from the pick list —
  // granting one does nothing until the module comes back, and a screen of
  // switches that quietly have no effect is how "I gave them the permission and
  // it still doesn't work" starts.
  //
  // But a code the role ALREADY holds is always shown, even from a module that
  // is off. The role really does grant it, it becomes live the moment the module
  // is switched back on, and hiding real access is worse than showing a row with
  // a marker on it.
  const shown = useMemo(() => {
    const source = editing && all ? all : codes;
    if (!moduleOff) return source;
    return source.filter((c) => granted.has(c) || !moduleOff(c));
  }, [editing, all, codes, moduleOff, granted]);

  const grouped = useMemo(() => {
    const by = new Map<string, string[]>();
    for (const c of [...shown].sort()) {
      const g = groupOf(c);
      by.set(g, [...(by.get(g) ?? []), c]);
    }
    return [...by.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [shown, groupOf]);

  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
      {grouped.map(([group, list]) => (
        <div key={group} className="rounded-lg border border-border bg-bg-subtle/40 p-3">
          <p className="mb-1.5 text-xs font-bold uppercase tracking-wide text-fg-muted">{group}</p>
          <ul className="flex flex-col gap-0.5">
            {list.map((c) => {
              const may = !held || held.includes(c);
              const off = moduleOff?.(c) ?? false;
              const label = (
                <>
                  <span>{labelOf(c)}</span>
                  {off ? <span className="ms-1 text-[10px] text-warning">{t_moduleOff}</span> : null}
                </>
              );
              return (
                <li key={c} className="text-sm text-fg-muted" title={c}>
                  {editing ? (
                    <label className={`flex items-center gap-2 ${may ? '' : 'opacity-50'}`}>
                      <input
                        type="checkbox"
                        checked={granted.has(c)}
                        disabled={!may}
                        title={may ? c : t_cannotGrant}
                        onChange={() => onToggle?.(c)}
                      />
                      {label}
                    </label>
                  ) : (
                    label
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </div>
  );
}

/** Filled in by the page so the grid can label rows without i18n wiring. */
let t_cannotGrant = '';
let t_moduleOff = '';

export function RolesPage(): React.JSX.Element {
  const { t } = useTranslation('permissions');
  const { t: tc } = useTranslation();
  const canWrite = usePermission('roles.write');
  const held = useAuthStore((s) => s.permissions);

  const [roles, setRoles] = useState<RoleDetail[] | null>(null);
  const [installing, setInstalling] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  /** The role being edited, and its working set of codes. */
  const [editing, setEditing] = useState<{ id: string; codes: string[] } | null>(null);
  const [savingRole, setSavingRole] = useState(false);
  const [allCodes, setAllCodes] = useState<string[]>([]);
  t_cannotGrant = t('page.cannotGrant');
  t_moduleOff = t('page.moduleOff');

  // Which add-ons this shop has switched off, so their permissions can drop out
  // of the pick list. Core domains are never gated — and several modules ride on
  // somebody else's permissions (expenses on gl.*, stocktake on stock.*), which
  // is why moduleForPermission returns null for those.
  const tenantId = useAuthStore((s) => s.tenant?.id);
  const enabledModules = useEnabledModules(tenantId);
  const moduleOff = useMemo(() => (code: string): boolean => {
    const key = moduleForPermission(code);
    return key !== null && !moduleEnabled(enabledModules, key);
  }, [enabledModules]);

  const load = async (): Promise<void> => {
    const r = await api().get<{ items: RoleDetail[] }>('/roles', { params: { detail: '1' } });
    setRoles(r.data.items);
  };
  useEffect(() => { void load(); }, []);
  useEffect(() => {
    // The whole catalogue, so the editor can offer codes the role does NOT yet
    // have. This is the first caller GET /permissions has ever had.
    void api().get<{ items: Array<{ code: string }> }>('/permissions')
      .then((r) => setAllCodes(r.data.items.map((p) => p.code)))
      .catch(() => setAllCodes([]));
  }, []);

  const saveRole = async (): Promise<void> => {
    if (!editing) return;
    setSavingRole(true);
    try {
      await api().patch(`/roles/${editing.id}`, { permissionCodes: editing.codes });
      toast.success(t('page.savedRole'));
      setEditing(null);
      await load();
    } catch (e) {
      const body = (e as { response?: { data?: { cause?: { code?: string } } } }).response?.data;
      const code = body?.cause?.code;
      if (code === 'roles.cannot_grant_unheld' || code === 'roles.cannot_revoke_unheld') {
        toast.error(t('page.cannotGrant'));
      } else if (code === 'users.last_admin') {
        toast.error(t('page.wouldLockOut'));
      } else {
        toast.error(tc('errors:server', { defaultValue: 'Something went wrong.' }));
      }
    } finally {
      setSavingRole(false);
    }
  };

  const byCode = useMemo(
    () => new Map((roles ?? []).map((r) => [r.code, r])),
    [roles],
  );

  /**
   * Install a preset through the ordinary create endpoint, which find-or-creates
   * and REFUSES to widen a role that already exists (409 `roles.code_taken`,
   * carrying what it would have granted). That refusal is the whole reason
   * presets can ship before a role editor does: adding one can create something
   * new, but it can never quietly re-permission people who already hold a role.
   */
  const install = async (preset: RolePreset): Promise<void> => {
    setInstalling(preset.code);
    try {
      await api().post('/roles', {
        code: preset.code,
        name: t(`presets.${preset.code}`, { defaultValue: preset.defaultName }),
        permissionCodes: [...preset.permissionCodes],
        // Which apps the role opens. Only 'till' is a real boundary — the till
        // proves itself with a device secret issued at pairing; the others are
        // what the client says it is.
        ...(preset.surfaces ? { surfaces: [...preset.surfaces] } : {}),
      });
      toast.success(t('page.installedToast'));
      await load();
    } catch (e) {
      const body = (e as { response?: { data?: { cause?: { code?: string; wouldGrant?: string[] } } } })
        .response?.data;
      if (body?.cause?.code === 'roles.code_taken') {
        toast.error(t('page.differs'), {
          description: t('page.differsDetail', { codes: (body.cause.wouldGrant ?? []).join(', ') }),
        });
      } else if (body?.cause?.code === 'roles.cannot_grant_unheld') {
        toast.error(t('page.cannotGrant'));
      } else {
        toast.error(tc('errors:server', { defaultValue: 'Something went wrong.' }));
      }
    } finally {
      setInstalling(null);
    }
  };

  return (
    <div className="mx-auto flex w-full max-w-6xl flex-col gap-4">
      <header className="flex items-center gap-3">
        <span className="grid h-10 w-10 place-items-center rounded-xl bg-brand/10 text-brand">
          <ShieldCheck className="h-5 w-5" aria-hidden />
        </span>
        <div>
          <h1 className="text-2xl font-semibold">{t('page.title')}</h1>
          <p className="text-sm text-fg-muted">{t('page.subtitle')}</p>
        </div>
      </header>

      {canWrite && (
        <Card className="p-6">
          <h2 className="mb-1 flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-fg-muted">
            <KeyRound className="h-4 w-4" aria-hidden />
            {t('page.presets')}
          </h2>
          <p className="mb-4 text-xs text-fg-muted">{t('page.presetsHint')}</p>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {/* A register cashier is meaningless with POS switched off, and a
                till cashier with the rep module off. Hidden rather than shown
                and refused. The dependency is declared on the preset, not
                guessed from its permissions — an Accountant holds
                attachments.read and a shop with files off still wants one. */}
            {ROLE_PRESETS.filter((p) => !p.module || moduleEnabled(enabledModules, p.module as never))
              .map((preset) => {
              const existing = byCode.get(preset.code);
              // A preset can only be installed by someone who already holds
              // everything in it — the same rule the server enforces, surfaced
              // here so the button explains itself instead of 403-ing.
              const missing = preset.permissionCodes.filter((c) => !(held ?? []).includes(c));
              return (
                <div key={preset.code} className="flex items-start gap-3 rounded-lg border border-border p-3">
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold">
                      {t(`presets.${preset.code}`, { defaultValue: preset.defaultName })}
                    </p>
                    <p className="text-xs text-fg-muted">
                      {t('page.permCount', { count: preset.permissionCodes.length })}
                      {preset.surfaces ? ` · ${preset.surfaces.join(', ')}` : ''}
                    </p>
                    {missing.length > 0 && (
                      <p className="mt-1 text-xs text-warning">{t('page.cannotGrant')}</p>
                    )}
                  </div>
                  {existing ? (
                    <Badge tone="success" className="flex-none gap-1">
                      <Check className="h-3 w-3" aria-hidden />
                      {t('page.installed')}
                    </Badge>
                  ) : (
                    <Button
                      type="button"
                      size="sm"
                      variant="secondary"
                      className="flex-none"
                      loading={installing === preset.code}
                      disabled={installing !== null || missing.length > 0}
                      onClick={() => void install(preset)}
                    >
                      <Plus className="h-4 w-4" aria-hidden />
                      {t('page.install')}
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        </Card>
      )}

      <Card className="p-6">
        {roles === null ? (
          <div className="flex flex-col gap-2">
            <Skeleton className="h-6 w-48" />
            <Skeleton className="h-24 w-full" />
          </div>
        ) : roles.length === 0 ? (
          <EmptyState title={t('page.empty')} />
        ) : (
          <ul className="flex flex-col gap-2">
            {roles.map((role) => (
              <li key={role.id} className="rounded-lg border border-border">
                <button
                  type="button"
                  className="flex w-full items-center gap-3 p-3 text-start hover:bg-bg-subtle/60"
                  onClick={() => setExpanded(expanded === role.id ? null : role.id)}
                  aria-expanded={expanded === role.id}
                >
                  <div className="min-w-0 flex-1">
                    <p className="flex items-center gap-2 font-semibold">
                      {role.name}
                      {SYSTEM_ROLE_CODES.has(role.code) && (
                        <Badge tone="neutral" className="text-[10px]">{t('page.systemRole')}</Badge>
                      )}
                    </p>
                    <p className="font-mono text-xs text-fg-subtle">{role.code}</p>
                  </div>
                  <span className="flex flex-none items-center gap-1 text-xs text-fg-muted">
                    <Users className="h-3.5 w-3.5" aria-hidden />
                    {(role.userCount ?? 0) > 0
                      ? t('page.holders', { count: role.userCount ?? 0 })
                      : t('page.noHolders')}
                  </span>
                  <span className="flex-none text-xs text-fg-muted">
                    {t('page.permCount', { count: role.permissionCodes?.length ?? 0 })}
                  </span>
                </button>
                {expanded === role.id && (
                  <div className="border-t border-border p-3">
                    <div className="mb-3 flex flex-wrap items-center gap-2">
                      {editing?.id === role.id ? (
                        <>
                          <Button type="button" size="sm" loading={savingRole} onClick={() => void saveRole()}>
                            {tc('actions.save', { defaultValue: 'Save' })}
                          </Button>
                          <Button type="button" size="sm" variant="secondary" disabled={savingRole} onClick={() => setEditing(null)}>
                            {tc('actions.cancel', { defaultValue: 'Cancel' })}
                          </Button>
                          <span className="text-xs text-fg-muted">
                            {t('page.permCount', { count: editing.codes.length })}
                          </span>
                        </>
                      ) : (
                        canWrite && (
                          <Button
                            type="button"
                            size="sm"
                            variant="secondary"
                            onClick={() => setEditing({ id: role.id, codes: [...(role.permissionCodes ?? [])] })}
                          >
                            <Pencil className="h-4 w-4" aria-hidden />
                            {tc('actions.edit', { defaultValue: 'Edit' })}
                          </Button>
                        )
                      )}
                    </div>
                    <PermissionGrid
                      moduleOff={moduleOff}
                      codes={editing?.id === role.id ? editing.codes : (role.permissionCodes ?? [])}
                      all={editing?.id === role.id ? allCodes : undefined}
                      held={editing?.id === role.id ? held : undefined}
                      onToggle={editing?.id === role.id ? (c) => setEditing((cur) => (cur
                        ? { ...cur, codes: cur.codes.includes(c) ? cur.codes.filter((x) => x !== c) : [...cur.codes, c] }
                        : cur)) : undefined}
                    />
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}
